// emailCache — Firestore-backed cache for the Email Inbox page.
//
// Syncing over IMAP takes seconds (connect + fetch full bodies), which made
// the page feel slow on every visit. Instead, every message we've ever synced
// is kept in the `emails` collection: the page renders from Firestore
// instantly, then a background sync asks read-emails only for messages NEWER
// than the highest UID already cached (per folder) and appends them.
//
// Doc id: `${folder}-${uid}` — IMAP UIDs are stable and ascending per
// mailbox, so this is unique and lets the same message never be written twice.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as qLimit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import { EmailFolder, EmailRecord, fetchEmails } from './emailApi';
import { USE_SUPABASE, supabase, subscribeTable, msFromISO0, isoFromMs } from './supabaseClient';
import { hubTransport } from './tableHub';

const COL = 'emails';
const CACHE_LOAD_MAX = 400; // legacy full-window size (tests + non-paged callers)

// Paged inbox (2026-09-11). The page keeps ONE small live window — the newest
// EMAIL_PAGE messages, realtime-patched — and walks older mail in EMAIL_PAGE
// slices as the dispatcher scrolls (loadOlderEmails). One request per scroll
// step, never a 400-row download on open.
export const EMAIL_PAGE = 20;
// 'unread' is a VIEW of the inbox (folder=inbox, read=false), not a folder.
export type EmailTab = 'inbox' | 'starred' | 'sent' | 'unread';

// Firestore docs cap at 1 MB — huge HTML newsletters must not sink a write.
const BODY_TEXT_MAX = 50_000;
const BODY_HTML_MAX = 180_000;

// Columns the LIST needs. `body_html` is deliberately absent: it is ~80% of the
// payload (measured on staging, 400 rows: 11.2 MB with it, 2.2 MB without —
// 9.0 MB of HTML), it is re-downloaded on every realtime change, and the only
// thing that renders it is an expanded HTML-only message. loadEmailHtml fetches
// it per message on open instead.
//
// `body_text` STAYS in the list on purpose — classifyThread reads it to tell an
// order/payment thread from support (see services/emailThreads.ts); dropping it
// would send order mail back to the 'support' bucket, the exact regression that
// comment warns about.
export const EMAIL_LIST_COLS =
  'id, uid, message_id, folder, from_addr, from_name, to_addr, subject, date, ' +
  'snippet, body_text, read, starred, attachments, in_reply_to, refs, assigned_to, assigned_to_name, claimed_at';

export const emailDocId = (folder: EmailFolder, uid: number) => `${folder}-${uid}`;

// ── Supabase row ⇄ EmailRecord mapping (snake_case DB, camelCase app) ───────
function mapEmailRow(row: any): EmailRecord {
  return {
    uid: row.uid,
    messageId: row.message_id || '',
    folder: row.folder as EmailFolder,
    from: row.from_addr || '',
    fromName: row.from_name || '',
    to: row.to_addr || '',
    subject: row.subject || '',
    date: msFromISO0(row.date),
    snippet: row.snippet || '',
    bodyText: row.body_text || '',
    bodyHtml: row.body_html || '',
    read: !!row.read,
    // Only present when set, so an unstarred row maps to the same shape as before.
    ...(row.starred ? { starred: true } : {}),
    ...(Array.isArray(row.attachments) && row.attachments.length ? { attachments: row.attachments } : {}),
    inReplyTo: row.in_reply_to ?? undefined,
    references: row.refs ?? undefined,
    assignedTo: row.assigned_to ?? null,
    assignedToName: row.assigned_to_name ?? null,
    claimedAt: row.claimed_at == null ? null : msFromISO0(row.claimed_at),
  };
}

// Postgres parses the ENTIRE PostgREST request body as JSON and rejects any
// unpaired surrogate with "22P02 … Unicode low surrogate must follow a high
// surrogate", which kills the whole batch — not just the offending row. That
// is exactly what a fixed-length `.slice()` produces when the cut lands inside
// an emoji's surrogate pair, and customer mail is full of emoji, so entire
// syncs sank on production (2026-09-16) with no row ever landing. Drop the
// orphan halves; a clipped emoji simply disappears instead of poisoning JSON.
function stripLoneSurrogates(s: string): string {
  if (!/[\uD800-\uDFFF]/.test(s)) return s;
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const next = s.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        out += s[i] + s[i + 1];
        i++;
      }
      // else: high surrogate with no partner — drop it.
    } else if (c >= 0xdc00 && c <= 0xdfff) {
      // Low surrogate with no partner — drop it.
    } else {
      out += s[i];
    }
  }
  return out;
}

// Walks the whole row because the poison can sit in any field: a clipped
// subject, a snippet, an attachment filename inside the jsonb column.
function sanitizeJsonStrings<T>(v: T): T {
  if (typeof v === 'string') return stripLoneSurrogates(v) as unknown as T;
  if (Array.isArray(v)) return v.map(sanitizeJsonStrings) as unknown as T;
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      out[stripLoneSurrogates(k)] = sanitizeJsonStrings(val);
    }
    return out as unknown as T;
  }
  return v;
}

function toEmailRow(e: EmailRecord): any {
  return sanitizeJsonStrings({
    id: emailDocId(e.folder, e.uid),
    uid: e.uid,
    message_id: e.messageId || '',
    folder: e.folder,
    from_addr: e.from || '',
    from_name: e.fromName || '',
    to_addr: e.to || '',
    subject: e.subject || '',
    date: isoFromMs(e.date) ?? new Date().toISOString(),
    snippet: e.snippet || '',
    body_text: e.bodyText || '',
    body_html: e.bodyHtml || '',
    read: !!e.read,
    // Left out unless the record carries it: the sync upserts rows it fetched
    // over IMAP, and a missing key must never reset a star somebody set.
    ...(e.starred != null ? { starred: !!e.starred } : {}),
    ...(e.attachments ? { attachments: e.attachments } : {}),
    in_reply_to: e.inReplyTo ?? null,
    refs: e.references ?? null,
    assigned_to: e.assignedTo ?? null,
    assigned_to_name: e.assignedToName ?? null,
    claimed_at: e.claimedAt == null ? null : isoFromMs(e.claimedAt),
  });
}

export async function loadCachedEmails(): Promise<EmailRecord[]> {
  if (USE_SUPABASE) {
    const { data, error } = await supabase()
      .from('emails')
      .select(EMAIL_LIST_COLS)
      .order('date', { ascending: false })
      .limit(CACHE_LOAD_MAX);
    if (error) throw new Error(`emails: ${error.message}`);
    return (data || []).map(mapEmailRow);
  }
  const snap = await getDocs(
    query(collection(db, COL), orderBy('date', 'desc'), qLimit(CACHE_LOAD_MAX))
  );
  return snap.docs.map((d) => d.data() as EmailRecord);
}

// Live version of loadCachedEmails: every console (each dispatcher, admin)
// follows the shared cache in real time, so a message synced or sent on one
// machine appears on all the others without anyone pressing Sync.
export function subscribeCachedEmails(
  onEmails: (emails: EmailRecord[]) => void,
  onError?: (e: Error) => void,
  limit: number = CACHE_LOAD_MAX
): () => void {
  if (USE_SUPABASE) {
    return subscribeTable<EmailRecord>('emails', mapEmailRow, onEmails, {
      order: { column: 'date', ascending: false },
      limit,
      select: EMAIL_LIST_COLS,
      onError,
      // The tab's shared `emails` binding (services/tableHub): the inbox page,
      // the sidebar badge and the alerts watcher cost Realtime one policy
      // check per change, not four. List semantics are unchanged.
      external: hubTransport('emails'),
    });
  }
  return onSnapshot(
    query(collection(db, COL), orderBy('date', 'desc'), qLimit(limit)),
    (snap) => onEmails(snap.docs.map((d) => d.data() as EmailRecord)),
    (e) => onError?.(e as Error)
  );
}

// ── Paging & on-demand loads ─────────────────────────────────────────────────

// PostgREST filter values are comma/paren delimited and `%`/`_` are LIKE
// wildcards — strip anything that could break out of the pattern.
const likeSafe = (s: string) => (s || '').replace(/[%_,()"'\\]/g, ' ').replace(/\s+/g, ' ').trim();

// The next EMAIL_PAGE messages OLDER than `before` (ms) for one tab. `before`
// null = the newest slice. Inclusive on the boundary so two messages that
// share a timestamp can't fall between pages — the caller dedupes by id.
export async function loadOlderEmails(
  tab: EmailTab,
  before: number | null,
  limit: number = EMAIL_PAGE
): Promise<EmailRecord[]> {
  if (USE_SUPABASE) {
    let q = supabase().from('emails').select(EMAIL_LIST_COLS);
    q =
      tab === 'starred'
        ? q.eq('starred', true)
        : tab === 'unread'
          ? q.eq('folder', 'inbox').eq('read', false).neq('uid', 0)
          : q.eq('folder', tab);
    if (before != null) q = q.lte('date', isoFromMs(before) ?? new Date(before).toISOString());
    const { data, error } = await q.order('date', { ascending: false }).limit(limit);
    if (error) throw new Error(`emails: ${error.message}`);
    return (data || []).map(mapEmailRow);
  }
  const parts: any[] = [
    collection(db, COL),
    ...(tab === 'starred'
      ? [where('starred', '==', true)]
      : tab === 'unread'
        ? [where('folder', '==', 'inbox'), where('read', '==', false)]
        : [where('folder', '==', tab)]),
    orderBy('date', 'desc'),
  ];
  if (before != null) parts.push(startAfter(before));
  parts.push(qLimit(limit));
  const snap = await getDocs(query(parts[0], ...parts.slice(1)));
  return snap.docs.map((d) => d.data() as EmailRecord);
}

// Every message of ONE conversation, so opening a thread shows the whole story
// even when only its newest message was in the paged window. Matched the same
// way buildThreads keys a thread: subject core (Re:/Fwd: stripped) + the other
// party's address on either side. Supabase only — Firestore mode keeps the
// full window and needs nothing here.
export async function loadThreadMates(
  subjectCore: string,
  counterpart: string,
  limit = 100
): Promise<EmailRecord[]> {
  if (!USE_SUPABASE) return [];
  const cp = likeSafe(counterpart).toLowerCase();
  if (!cp) return [];
  const core = likeSafe(subjectCore);
  let q = supabase()
    .from('emails')
    .select(EMAIL_LIST_COLS)
    .or(`from_addr.ilike.${cp},to_addr.ilike.%${cp}%`);
  if (core && core !== '(no subject)') q = q.ilike('subject', `%${core}%`);
  const { data, error } = await q.order('date', { ascending: false }).limit(limit);
  if (error) throw new Error(`emails: ${error.message}`);
  return (data || []).map(mapEmailRow);
}

// Server-side search, Gmail-style: every word of the query must appear
// somewhere in the message — subject, sender, recipient, snippet OR the body
// (an order number quoted inside a reply, a tracking number, a street) — and
// it reaches mail that isn't in the paged window. One `.or(...)` per word;
// PostgREST ANDs successive filters. Firestore mode: no text search — the
// caller keeps filtering what it has.
export async function searchEmails(q: string, limit = 100): Promise<EmailRecord[]> {
  if (!USE_SUPABASE) return [];
  const words = likeSafe(q).split(' ').filter(Boolean);
  if (!words.length) return [];
  let query = supabase().from('emails').select(EMAIL_LIST_COLS);
  for (const w of words) {
    const pat = `%${w}%`;
    query = query.or(
      `subject.ilike.${pat},from_addr.ilike.${pat},from_name.ilike.${pat},to_addr.ilike.${pat},snippet.ilike.${pat},body_text.ilike.${pat}`
    );
  }
  const { data, error } = await query.order('date', { ascending: false }).limit(limit);
  if (error) throw new Error(`emails: ${error.message}`);
  return (data || []).map(mapEmailRow);
}

// Specific messages by cache id (deep links to mail outside the window).
export async function loadEmailsByIds(ids: string[]): Promise<EmailRecord[]> {
  const wanted = [...new Set(ids)].filter(Boolean);
  if (!wanted.length) return [];
  if (USE_SUPABASE) {
    const { data, error } = await supabase().from('emails').select(EMAIL_LIST_COLS).in('id', wanted);
    if (error) throw new Error(`emails: ${error.message}`);
    return (data || []).map(mapEmailRow);
  }
  const snaps = await Promise.all(wanted.map((id) => getDoc(doc(db, COL, id))));
  return snaps.filter((s) => s.exists()).map((s) => s.data() as EmailRecord);
}

// Whole-mailbox counters for the header / tab badges — the paged window can't
// count what it hasn't loaded. Two HEAD requests (count only, no rows). Null
// where unsupported so the caller falls back to counting the window.
export async function emailCounts(): Promise<{ unread: number; starred: number } | null> {
  if (!USE_SUPABASE) return null;
  const sb = supabase();
  const [u, s] = await Promise.all([
    // uid 0 is never a real message — it was the Firestore-era 'meta-sync'
    // lock doc, which the staging data load copied in as an unread row.
    sb.from('emails').select('id', { count: 'exact', head: true }).eq('folder', 'inbox').eq('read', false).neq('uid', 0),
    sb.from('emails').select('id', { count: 'exact', head: true }).eq('starred', true),
  ]);
  if (u.error) throw new Error(`emails: ${u.error.message}`);
  if (s.error) throw new Error(`emails: ${s.error.message}`);
  return { unread: u.count ?? 0, starred: s.count ?? 0 };
}

// Highest cached UID per folder, straight from the table. syncFolder used to
// read it off the rows on screen — with a 20-row window that would have
// re-fetched (and re-parsed into order cards) mail the cache already holds.
// Server-sync heartbeat. email-sync-cron stamps `settings`.emailServerSyncHeartbeat
// on every run that read the inbox successfully. A page in server-sync mode
// asks this before skipping its own poll: a server that has not checked in
// for SERVER_SYNC_STALE_MS is treated as absent and the old browser path
// takes over — so a switch left off, a paused schedule or a broken function
// can only ever cost the server's share of the work, never the mail.
// One read a minute per tab at most; the answer is memoised in between.
export const SERVER_SYNC_HEARTBEAT_KEY = 'emailServerSyncHeartbeat';
export const SERVER_SYNC_STALE_MS = 3 * 60 * 1000;
const HEARTBEAT_CHECK_MS = 60 * 1000;
let heartbeatCheckedAt = 0;
let heartbeatFresh = false;
export async function serverSyncAlive(): Promise<boolean> {
  if (!USE_SUPABASE) return false;
  if (Date.now() - heartbeatCheckedAt < HEARTBEAT_CHECK_MS) return heartbeatFresh;
  heartbeatCheckedAt = Date.now();
  try {
    const { data, error } = await supabase()
      .from('settings')
      .select('value')
      .eq('key', SERVER_SYNC_HEARTBEAT_KEY)
      .maybeSingle();
    if (error) throw error;
    const at = (data?.value as { at?: number } | null)?.at || 0;
    heartbeatFresh = Date.now() - at < SERVER_SYNC_STALE_MS;
  } catch {
    heartbeatFresh = false; // cannot tell → poll, the safe side
  }
  return heartbeatFresh;
}
/** Tests: forget the memoised answer. */
export function _resetServerSyncHeartbeat(): void {
  heartbeatCheckedAt = 0;
  heartbeatFresh = false;
}

export async function maxCachedUid(folder: EmailFolder): Promise<number> {
  if (!USE_SUPABASE) return 0;
  const { data, error } = await supabase()
    .from('emails')
    .select('uid')
    .eq('folder', folder)
    .gt('uid', 0)
    .order('uid', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(`emails: ${error.message}`);
  return (data?.uid as number) || 0;
}

// Short-lived signed URL for an attachment in the private 'email-media'
// bucket. Signed SERVER-SIDE (email-attachment-url.js, service role, dispatch
// auth) so it never depends on storage RLS or the browser's session shape.
// Firestore mode has no store for these.
export async function emailAttachmentUrl(path: string): Promise<string | null> {
  if (!USE_SUPABASE || !path) return null;
  const { data } = await supabase().auth.getSession();
  const token = data.session?.access_token;
  const res = await fetch(`/.netlify/functions/email-attachment-url?path=${encodeURIComponent(path)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = `attachment link (${res.status})`;
    try { msg = (await res.json()).error || msg; } catch { /* keep */ }
    throw new Error(msg);
  }
  return ((await res.json()) as { url?: string }).url || null;
}

// Bodies the list deliberately left behind (see EMAIL_LIST_COLS). Called when a
// message is expanded, for the handful of ids on screen — never for the whole
// cache. Returns id → html; ids with no stored HTML simply come back absent, so
// a caller can treat "missing" as "nothing to render" without a second check.
export async function loadEmailHtml(ids: string[]): Promise<Record<string, string>> {
  const wanted = [...new Set(ids)].filter(Boolean);
  if (!wanted.length) return {};
  const out: Record<string, string> = {};
  if (USE_SUPABASE) {
    const { data, error } = await supabase()
      .from('emails')
      .select('id, body_html')
      .in('id', wanted);
    if (error) throw new Error(`emails html: ${error.message}`);
    for (const r of data || []) if (r.body_html) out[r.id as string] = r.body_html as string;
    return out;
  }
  const snaps = await Promise.all(wanted.map((id) => getDoc(doc(db, COL, id))));
  for (const s of snaps) {
    const html = s.exists() ? ((s.data() as EmailRecord).bodyHtml || '') : '';
    if (html) out[s.id] = html;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Sent-mail mirroring.
//
// SMTP acceptance and the copy landing in the IMAP Sent folder are separate
// events (Gmail files it a few seconds later). Waiting for a sync meant a sent
// email was invisible to every other console until someone pressed Sync. So:
// the moment a send succeeds, the message is written into the cache as a
// PROVISIONAL record (uid < 0, keyed off the SMTP Message-ID); when the real
// Sent-folder copy arrives, syncFolder swaps the two in one batch.
// ---------------------------------------------------------------------------

// Deterministic small hash of the Message-ID → stable negative uid, so a
// double write of the same send can't create two provisional docs.
function provisionalUid(messageId: string): number {
  let h = 0;
  for (let i = 0; i < messageId.length; i++) h = (h * 31 + messageId.charCodeAt(i)) | 0;
  return -(Math.abs(h) + 1);
}

// Returns the record it wrote so callers can feed it straight into the
// order-card parser without waiting for the IMAP copy.
export async function cacheSentEmail(input: {
  to: string;
  subject: string;
  text: string;
  messageId: string;
  // Reply headers of the send, so the echo threads with the conversation it
  // answers instead of standing alone until the Sent-folder copy arrives.
  inReplyTo?: string;
  references?: string[];
}): Promise<EmailRecord> {
  const uid = provisionalUid(input.messageId || `${input.to}-${input.subject}-${Date.now()}`);
  const rec: EmailRecord = {
    uid,
    messageId: input.messageId || '',
    folder: 'sent',
    from: '',
    fromName: '',
    to: input.to,
    subject: input.subject,
    date: Date.now(),
    snippet: input.text.replace(/\s+/g, ' ').trim().slice(0, 140),
    bodyText: input.text.slice(0, BODY_TEXT_MAX),
    bodyHtml: '',
    read: true,
    ...(input.inReplyTo ? { inReplyTo: input.inReplyTo } : {}),
    ...(input.references?.length ? { references: input.references } : {}),
  };
  if (USE_SUPABASE) {
    // B13 (mirrors B11): the Sent-folder copy that syncFolder ingests over IMAP is
    // the ONE persistent row. Do NOT also persist a provisional here — the second
    // write can't be reconciled reliably over PostgREST's non-atomic, one-shot swap
    // + eventual-consistency re-fetch, so both rows survived and the send showed
    // twice. Return the record so the caller creates the board card + echoes it
    // locally (see EmailInbox optimistic echo).
    return rec;
  }
  await setDoc(doc(db, COL, emailDocId('sent', uid)), rec);
  return rec;
}

// The read-emails function must finish inside Netlify's 10s limit, so the
// very first sync (empty cache → full bodies over IMAP) stays small; once
// cached, incremental syncs only carry genuinely new messages.
const FIRST_SYNC_LIMIT = 15;
// Per IMAP call. Smaller than before so a batch with full bodies stays well
// inside the function's 10s budget; a backlog is drained across several calls
// (SYNC_MAX_ROUNDS) in ONE sync instead of one oversized call.
const INCREMENTAL_LIMIT = 25;
const SYNC_MAX_ROUNDS = 8;

// Fleet-wide sync throttle. Every open console auto-syncs on a timer; without
// coordination, N consoles = N× the IMAP logins per minute and Gmail starts
// refusing connections (the "Could not load emails (500)" banners). The last
// sync time is shared in the emails collection itself — the doc has NO `date`
// field, so the inbox list query (orderBy date) never sees it and the
// existing security rules cover it. Best-effort: a race just means two syncs,
// which is exactly what happened on every timer tick before.
const SYNC_META_ID = 'meta-sync';

// `key` lets the inbox and the Sent folder throttle independently — the inbox
// polls every few seconds, Sent only once a minute (our own sends are echoed
// locally and re-synced right after sending).
/** A hidden tab (another window in front, the machine left overnight) still
 *  syncs so alerts keep coming, but no more often than this — a console left
 *  open all night was the biggest single writer of the lock (2026-09-22). */
export const HIDDEN_TAB_MIN_GAP_MS = 5 * 60 * 1000;

// One warning per lock key per tab, not one per tick.
const lockWarned = new Set<string>();
function warnLockOnce(key: string, what: string): void {
  if (lockWarned.has(key)) return;
  lockWarned.add(key);
  console.warn(`[emailCache] sync lock "${key}" ${what}; this tab will not sync until the lock works`);
}

export async function shouldBackgroundSync(minGapMs: number, key = 'emailSyncLock'): Promise<boolean> {
  if (USE_SUPABASE) {
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const gap = hidden ? Math.max(minGapMs, HIDDEN_TAB_MIN_GAP_MS) : minGapMs;
    // NO LOCK, NO SYNC (2026-09-23). The old fallback — "the lock could not be
    // read or written, sync anyway rather than go stale" — meant that on a site
    // where the lock did not work, EVERY open console ran the full mail sync on
    // its own clock, every tick: the exact load the lock exists to prevent, and
    // the reason a missing migration 0085 showed up as a database-wide slowdown
    // rather than as a stale inbox. A tab that cannot prove nobody else is
    // syncing stays quiet, says so once, and asks again next tick; a console
    // that CAN take the lock syncs for everyone.
    try {
      const { data, error: readError } = await supabase()
        .from('settings')
        .select('value')
        .eq('key', key)
        .maybeSingle();
      if (readError) throw readError;
      const last = (data?.value as { at?: number } | null)?.at || 0;
      if (Date.now() - last < gap) return false;
      const { error } = await supabase().from('settings').upsert({ key, value: { at: Date.now() } });
      if (error) {
        // 42501 = this role may not write the lock (migration 0085 missing).
        warnLockOnce(key, `could not be written (${error.code || error.message})`);
        return false;
      }
      return true;
    } catch (e: any) {
      warnLockOnce(key, `could not be read (${e?.message || e})`);
      return false;
    }
  }
  try {
    const ref = doc(db, COL, key === 'emailSyncLock' ? SYNC_META_ID : `${SYNC_META_ID}-${key.split(':').pop()}`);
    const snap = await getDoc(ref);
    const last = snap.exists() ? ((snap.data() as { lastSyncAt?: number }).lastSyncAt || 0) : 0;
    if (Date.now() - last < minGapMs) return false;
    await setDoc(ref, { lastSyncAt: Date.now() }, { merge: true });
    return true;
  } catch {
    return true; // meta unreadable — sync anyway rather than go stale
  }
}

// Pull messages newer than what's cached for one folder, persist them, and
// return just the new ones (caller merges into UI state).
export async function syncFolder(
  folder: EmailFolder,
  cached: EmailRecord[],
  // `maxUid`: the folder's true high-water mark (see maxCachedUid) when the
  // caller only holds a window of the cache. The rows on hand still win if
  // they happen to be newer.
  hints?: { maxUid?: number }
): Promise<EmailRecord[]> {
  const inFolder = cached.filter((e) => e.folder === folder);
  let maxUid = inFolder.reduce((m, e) => Math.max(m, e.uid || 0), hints?.maxUid || 0);
  // Queue drain: the function returns the OLDEST batch above maxUid. A full
  // batch means more is waiting, so advance the mark to the last one received
  // and ask again — up to SYNC_MAX_ROUNDS per sync; whatever is left simply
  // heads the next sync. Nothing between two syncs is ever skipped.
  const fresh: EmailRecord[] = [];
  for (let round = 0; round < SYNC_MAX_ROUNDS; round++) {
    const batchLimit = maxUid > 0 ? INCREMENTAL_LIMIT : FIRST_SYNC_LIMIT;
    const batch = await fetchEmails(batchLimit, folder, maxUid);
    fresh.push(...batch);
    const top = batch.reduce((m, e) => Math.max(m, e.uid || 0), 0);
    if (maxUid === 0 || batch.length < batchLimit || top <= maxUid) break;
    maxUid = top;
  }

  const have = new Set(inFolder.map((e) => e.uid));
  // Permanent-ID dedup on top of the UID check: IMAP UIDs can shift when a
  // mailbox reshuffles, but the RFC Message-ID never changes. A message whose
  // Message-ID is already cached is the SAME email wearing a new UID — skip it
  // instead of duplicating it (and re-parsing it into a duplicate order card).
  // Provisional send-time copies (uid < 0) are deliberately excluded: their
  // real Sent-folder twin must still come through so the swap below replaces
  // them rather than being filtered out here.
  const haveMsgIds = new Set(
    inFolder.filter((e) => e.uid > 0 && e.messageId).map((e) => e.messageId)
  );
  const newOnes = fresh.filter(
    (e) => e.uid && !have.has(e.uid) && !(e.messageId && haveMsgIds.has(e.messageId))
  );
  if (newOnes.length === 0) return [];

  if (USE_SUPABASE) {
    const provisionalSb = inFolder.filter((e) => e.uid < 0 && e.messageId);
    const rows = newOnes.map((e) =>
      toEmailRow({
        ...e,
        folder,
        bodyText: (e.bodyText || '').slice(0, BODY_TEXT_MAX),
        bodyHtml: (e.bodyHtml || '').slice(0, BODY_HTML_MAX),
      })
    );
    const deleteIds = newOnes
      .map((e) => provisionalSb.find((p) => p.messageId && p.messageId === e.messageId))
      .filter((t): t is EmailRecord => !!t)
      .map((t) => emailDocId(folder, t.uid));
    // PostgREST derives ONE column list for a bulk upsert — the union of the
    // objects' keys — and substitutes an EMPTY STRING for any row missing one
    // of them, which a jsonb column rejects with "invalid input syntax for
    // type json". toRow deliberately omits `starred` and `attachments` when a
    // record does not carry them (so a sync never resets somebody's star or
    // drops stored files), so a mixed-shape batch is the normal case — and it
    // sank every Sent write on production until 2026-09-16, silently, because
    // the throw only surfaces on a manual sync. Group by key shape and send
    // one upsert per shape: same semantics, nothing to substitute.
    const shapes = new Map<string, typeof rows>();
    for (const r of rows) {
      const shape = Object.keys(r).sort().join(',');
      const bucket = shapes.get(shape);
      if (bucket) bucket.push(r);
      else shapes.set(shape, [r]);
    }
    for (const bucket of shapes.values()) {
      const { error } = await supabase().from('emails').upsert(bucket);
      if (error) throw new Error(`emails: ${error.message}`);
    }
    if (deleteIds.length > 0) {
      const { error: delErr } = await supabase().from('emails').delete().in('id', deleteIds);
      if (delErr) throw new Error(`emails: ${delErr.message}`);
    }
    return newOnes;
  }

  const batch = writeBatch(db);
  // Provisional copies written by cacheSentEmail at send time (uid < 0) are
  // replaced by the real Sent-folder message in this same batch, so the swap
  // never shows a duplicate.
  const provisional = inFolder.filter((e) => e.uid < 0 && e.messageId);
  for (const e of newOnes) {
    batch.set(doc(db, COL, emailDocId(folder, e.uid)), {
      ...e,
      folder,
      bodyText: (e.bodyText || '').slice(0, BODY_TEXT_MAX),
      bodyHtml: (e.bodyHtml || '').slice(0, BODY_HTML_MAX),
    });
    const twin = provisional.find((p) => p.messageId && p.messageId === e.messageId);
    if (twin) batch.delete(doc(db, COL, emailDocId(folder, twin.uid)));
  }
  await batch.commit();
  return newOnes;
}

// ---------------------------------------------------------------------------
// Read-state — persisted so an email opened by one dispatcher shows read for
// all, and safe to mark while the connection is down.
//
// Client report (production, 2026-09-24): mail opened while the internet was
// out went back to unread after a refresh. markEmailRead fired one direct
// update per message and dropped it on failure, so those flags never landed.
// Now a doc id is parked in this in-memory set until the server has taken it
// (a few bytes per id; nothing on disk, nothing extra in the DB). A flush —
// on every read, on the browser's `online` event and on every sync — sends
// ALL parked ids in ONE `in (ids)` update, so a reconnect costs one request
// however many threads were opened offline. Only "no answer at all" (status
// 0: offline, socket dropped) is retried; a server verdict is final. The set
// is capped so a long outage can never grow it without bound.
const PENDING_READ_MAX = 500;
const pendingReadIds = new Set<string>();
let readFlush: Promise<void> | null = null;

/** Ids marked read that the server has not confirmed yet (tests/diagnostics). */
export function pendingReadCount(): number {
  return pendingReadIds.size;
}

/** Send every parked read flag in one update. No-op when nothing is parked. */
export function flushPendingReads(): Promise<void> {
  if (!USE_SUPABASE || pendingReadIds.size === 0) return Promise.resolve();
  if (readFlush) return readFlush; // one in flight at a time — never stack
  const ids = Array.from(pendingReadIds);
  let delivered = false;
  readFlush = Promise.resolve(
    supabase()
      .from('emails')
      .update({ read: true })
      .in('id', ids)
      .then(
        (res: { error: unknown; status?: number }) => {
          delivered = !(res.error && res.status === 0);
        },
        () => {} // thrown = no answer either; keep for the next flush
      )
  ).then(() => {
    readFlush = null;
    if (!delivered) return;
    ids.forEach((id) => pendingReadIds.delete(id));
    // Reads marked while that batch was in flight go out right away.
    if (pendingReadIds.size) void flushPendingReads();
  });
  return readFlush;
}

export function markEmailRead(folder: EmailFolder, uid: number): void {
  if (USE_SUPABASE) {
    if (pendingReadIds.size >= PENDING_READ_MAX) return; // cap reached: best effort only
    pendingReadIds.add(emailDocId(folder, uid));
    void flushPendingReads();
    return;
  }
  updateDoc(doc(db, COL, emailDocId(folder, uid)), { read: true }).catch(() => {});
}

// Gmail-style star. Shared state like read-state: every console sees it. One
// call covers a whole conversation (star = the newest message, unstar = every
// starred member), so the caller passes the exact ids to flip.
export async function setEmailsStarred(
  items: { folder: EmailFolder; uid: number }[],
  starred: boolean
): Promise<void> {
  const ids = items.map((m) => emailDocId(m.folder, m.uid));
  if (ids.length === 0) return;
  if (USE_SUPABASE) {
    const { error } = await supabase().from('emails').update({ starred }).in('id', ids);
    if (error) throw new Error(`emails: ${error.message}`);
    return;
  }
  const batch = writeBatch(db);
  for (const id of ids) batch.update(doc(db, COL, id), { starred });
  await batch.commit();
}

// ---------------------------------------------------------------------------
// Claim queue — one conversation, one owner.
//
// The claim lives on the thread's INBOX message docs (the `emails` collection
// the existing rules already cover — no new collection, no rules change).
// Atomicity: the transaction reads the thread's anchor (newest inbox message);
// if someone else claimed it first, the transaction fails and the caller shows
// "already claimed". Two dispatchers can never win the same conversation.
// ---------------------------------------------------------------------------

export class AlreadyClaimedError extends Error {
  constructor(public byName: string) {
    super(`Already claimed by ${byName}`);
  }
}

export async function claimThread(
  anchor: EmailRecord,
  memberDocs: { folder: EmailFolder; uid: number }[],
  user: { id: string; name: string }
): Promise<void> {
  if (USE_SUPABASE) {
    const anchorId = emailDocId(anchor.folder, anchor.uid);
    // No client-side transactions over PostgREST — emulate the claim race with
    // a guarded UPDATE on the anchor: it only sticks if unclaimed (or already
    // ours). Zero rows back = somebody else holds it (or it left the cache).
    const fields = {
      assigned_to: user.id,
      assigned_to_name: user.name,
      claimed_at: isoFromMs(Date.now()),
    };
    const { data: won, error } = await supabase()
      .from('emails')
      .update(fields)
      .eq('id', anchorId)
      .or(`assigned_to.is.null,assigned_to.eq.${user.id}`)
      .select('id');
    if (error) throw new Error(`emails: ${error.message}`);
    if (!won || won.length === 0) {
      const { data: cur } = await supabase()
        .from('emails')
        .select('assigned_to, assigned_to_name')
        .eq('id', anchorId)
        .maybeSingle();
      if (!cur) throw new Error('This conversation is no longer in the cache — sync and retry');
      throw new AlreadyClaimedError(cur.assigned_to_name || 'another dispatcher');
    }
    const restIds = memberDocs
      .map((m) => emailDocId(m.folder, m.uid))
      .filter((id) => id !== anchorId);
    if (restIds.length > 0) {
      const { error: e2 } = await supabase().from('emails').update(fields).in('id', restIds);
      if (e2) throw new Error(`emails: ${e2.message}`);
    }
    return;
  }
  const anchorRef = doc(db, COL, emailDocId(anchor.folder, anchor.uid));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(anchorRef);
    if (!snap.exists()) throw new Error('This conversation is no longer in the cache — sync and retry');
    const data = snap.data() as EmailRecord;
    if (data.assignedTo && data.assignedTo !== user.id) {
      throw new AlreadyClaimedError(data.assignedToName || 'another dispatcher');
    }
    const fields = { assignedTo: user.id, assignedToName: user.name, claimedAt: Date.now() };
    for (const m of memberDocs) {
      tx.update(doc(db, COL, emailDocId(m.folder, m.uid)), fields);
    }
  });
}

// Put a conversation back in the shared queue (or, for an admin, take it off
// someone). Callers gate who may release — rules allow any dispatch-level user.
export async function releaseThread(memberDocs: { folder: EmailFolder; uid: number }[]): Promise<void> {
  if (USE_SUPABASE) {
    const ids = memberDocs.map((m) => emailDocId(m.folder, m.uid));
    if (ids.length === 0) return;
    const { error } = await supabase()
      .from('emails')
      .update({ assigned_to: null, assigned_to_name: null, claimed_at: null })
      .in('id', ids);
    if (error) throw new Error(`emails: ${error.message}`);
    return;
  }
  const batch = writeBatch(db);
  const fields = { assignedTo: null, assignedToName: null, claimedAt: null };
  for (const m of memberDocs) {
    batch.update(doc(db, COL, emailDocId(m.folder, m.uid)), fields);
  }
  await batch.commit();
}

// Supervisor override: force a conversation onto a specific owner regardless of
// who holds it now. Unlike claimThread (which fails if already owned), this is
// the admin "reassign without using another person's account" action — it just
// sets the owner across the thread's message docs. Gating is the caller's job
// (the Dispatch Oversight page is admin/super_admin only).
export async function reassignThread(
  memberDocs: { folder: EmailFolder; uid: number }[],
  owner: { id: string; name: string }
): Promise<void> {
  if (USE_SUPABASE) {
    const ids = memberDocs.map((m) => emailDocId(m.folder, m.uid));
    if (ids.length === 0) return;
    const { error } = await supabase()
      .from('emails')
      .update({
        assigned_to: owner.id,
        assigned_to_name: owner.name,
        claimed_at: isoFromMs(Date.now()),
      })
      .in('id', ids);
    if (error) throw new Error(`emails: ${error.message}`);
    return;
  }
  const batch = writeBatch(db);
  const fields = { assignedTo: owner.id, assignedToName: owner.name, claimedAt: Date.now() };
  for (const m of memberDocs) {
    batch.update(doc(db, COL, emailDocId(m.folder, m.uid)), fields);
  }
  await batch.commit();
}
